import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { useAuth } from "../auth/auth-context";
import { landing } from "./landing";

/**
 * Where the apex chooser lands: the practice they picked, spending its
 * hand-off (2026-09-20).
 *
 * ── Why a whole page for one exchange ───────────────────────────────────────
 *
 * The same reason `WelcomePage` exists, and it is worth restating because the
 * two look like duplicates and are not. The browser genuinely changed origin:
 * the chooser ran at the apex, the session has to live at
 * `their-practice.dentacore.uk`, and `localStorage` does not cross between
 * them. So this is a real second page load, not a route transition, and
 * something has to be on screen while the token is spent.
 *
 * ── What it is not ──────────────────────────────────────────────────────────
 *
 * Not a password prompt. That is the entire point of the change: the password
 * was verified at the apex *before* the practices were named — it had to be,
 * because naming them is the disclosure — so asking again here protected
 * nothing and the client said as much.
 *
 * ── The failure case is the one worth designing ─────────────────────────────
 *
 * A chooser hand-off lives sixty seconds. Seeing it expire means walking away
 * mid-click, at which point the practice **exists** and the password **works**
 * — so this is a signpost, not an error, and it points at the sign-in on this
 * origin rather than back at the apex. They are already where they wanted to
 * be; one password gets them in.
 */
export function EnterPage(): React.JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { signInWithChoice } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  /**
   * React runs effects twice in development's strict mode. A hand-off may be
   * spent twice inside its minute, so the second call would *succeed* and mint
   * a second session rather than showing an error — quieter than the sign-up
   * case and just as wrong. Guarded on a ref rather than state, because state
   * updates are not synchronous enough to stop the second run.
   */
  const spent = useRef(false);

  useEffect(() => {
    if (token === undefined || spent.current) return;
    spent.current = true;

    signInWithChoice(token)
      .then(async (signedIn) => {
        // The same landing sign-in uses, so the door somebody came through
        // cannot change where they end up — an admin whose practice is still
        // half configured gets `/setup` here exactly as they would there.
        await navigate(await landing(signedIn), { replace: true });
      })
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught.message : "Could not reach the server.");
      });
  }, [token, signInWithChoice, navigate]);

  if (error !== null) {
    return (
      <main className="login">
        <h1>Almost there</h1>
        <p role="alert" data-testid="enter-error">
          {error}
        </p>
        <p className="quiet">
          Nothing is wrong with your account — this link is a one-time shortcut and it has been used
          or has expired. <Link to="/login">Sign in</Link> here with the password you already use.
        </p>
      </main>
    );
  }

  return (
    <main className="login">
      <h1>Signing you in</h1>
      <p className="quiet">One moment.</p>
    </main>
  );
}
